<template>
  <v-dialog
    v-model="internalValue"
    :max-width="maxWidth"
    :persistent="persistent"
    content-class="premium-modal-wrap"
    scrollable
    transition="dialog-bottom-transition"
  >
    <v-card
      class="premium-modal-card rounded-xl overflow-hidden"
      :color="isDark ? '#1e293b' : 'white'"
    >
      <!-- Header -->
      <v-card-title
        class="premium-modal-header pa-5 px-6 d-flex align-center justify-space-between"
        :style="isDark ? 'background: #1e293b !important;' : 'background: #ffffff !important;'"
      >
        <div class="modal-title-text">{{ title }}</div>
        <v-btn
          icon
          @click="close"
          class="modal-close-btn"
          :color="isDark ? 'white' : 'grey darken-1'"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
 
      <v-divider :style="isDark ? 'border-color: rgba(255,255,255,0.1);' : 'border-color: rgba(0,0,0,0.08);'" />
 
      <!-- Body -->
      <v-card-text
        class="pa-0 modal-scroll-body"
        :style="isDark ? 'background: #1e293b !important;' : 'background: #ffffff !important;'"
      >
        <div class="pa-6">
          <slot />
        </div>
      </v-card-text>
 
      <v-divider :style="isDark ? 'border-color: rgba(255,255,255,0.1);' : 'border-color: rgba(0,0,0,0.08);'" />
 
      <!-- Footer -->
      <v-card-actions
        class="pa-5 px-6 modal-actions-foot d-flex align-center justify-start"
        :style="isDark ? 'background: #1e293b !important;' : 'background: #ffffff !important;'"
      >
        <v-btn
          v-if="cancelText !== ''"
          text
          @click="onCancel"
          class="mr-2 btn-premium-cancel"
          :disabled="cancelDisabled"
        >
          {{ cancelText || "Cancel" }}
        </v-btn>
        <v-btn
          v-if="submitText"
          depressed
          @click="$emit('submit')"
          :loading="loading"
          class="btn-premium-primary ml-3"
          :style="{ '--btn-color': btnColor }"
        >
          {{ submitText || "Submit" }}
        </v-btn>
        <v-spacer />
        <slot name="footer-extra" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
 
<script>
import { mapState } from "vuex";
 
export default {
  name: "PremiumModal",
 
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, default: "Modal Title" },
    loading: { type: Boolean, default: false },
    submitText: { type: String, default: "Submit" },
    submitIcon: { type: String, default: "mdi-check" },
    cancelText: { type: String, default: "Cancel" },
    cancelDisabled: { type: Boolean, default: false },
    maxWidth: { type: [String, Number], default: 600 },
    persistent: { type: Boolean, default: false },
  },
 
  computed: {
    ...mapState("theme", ["isDark", "primaryColor", "buttonColor"]),
    btnColor() {
      return this.buttonColor || this.primaryColor || "#30384d";
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
 
  methods: {
    close() {
      this.internalValue = false;
      this.$emit("close");
    },
    onCancel() {
      this.internalValue = false;
      this.$emit("cancel");
      this.$emit("close");
    },
  },
};
</script>
 
<style scoped>
.modal-title-text {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--tx, #111827);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 8px;
}
 
.theme--dark .modal-title-text {
  color: #FAFAFA !important;
}
 
.modal-scroll-body {
  max-height: 70vh;
}
 
.premium-modal-card {
  border: 1px solid var(--border, rgba(0, 0, 0, 0.05)) !important;
}
 
.theme--dark .premium-modal-card {
  background: #1e293b !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* Force header, body, footer to match theme — Vuetify overrides these internally */
.theme--dark .premium-modal-header,
.theme--dark .modal-actions-foot {
  background: #1e293b !important;
}

.theme--dark .modal-scroll-body {
  background: #1e293b !important;
}

/* Light mode explicitly white */
.theme--light .premium-modal-header,
.theme--light .modal-actions-foot,
.theme--light .modal-scroll-body {
  background: #ffffff !important;
}
 
/* Close button: always 44×44 tap target on mobile */
.modal-close-btn {
  flex-shrink: 0;
  min-width: 44px !important;
  min-height: 44px !important;
}
 
/* ── Responsive ───────────────────────── */
 
/* Tablet (≤768px) */
@media (max-width: 768px) {
  .modal-scroll-body {
    max-height: 60vh;
  }
}
 
/* Mobile (≤600px) */
@media (max-width: 600px) {
  .modal-scroll-body {
    max-height: 55vh;
  }
 
  /* Reduce modal header & footer padding on phones */
  .premium-modal-header {
    padding: 14px 16px !important;
  }
 
  .modal-actions-foot {
    padding: 12px 16px !important;
    flex-wrap: wrap;
    gap: 8px;
  }
 
  /* Stack footer buttons on very small phones */
  .modal-actions-foot .v-btn {
    flex: 1;
    min-width: 0;
  }
 
  .modal-title-text {
    font-size: 14px;
  }
}
 
/* Small phones (≤400px) */
@media (max-width: 400px) {
  .modal-scroll-body {
    max-height: 50vh;
  }
 
  .premium-modal-header {
    padding: 12px 12px !important;
  }
 
  .modal-actions-foot {
    padding: 10px 12px !important;
  }
}
</style>

 